/** @jsxImportSource @emotion/react */

import { useMutation, useQuery } from '@apollo/client'
import { useParams, createSearchParams } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { useMemo } from 'react'
import omit from 'lodash/omit'
import slugify from 'slugify'
import { FIND_ACHIEVEMENT, UPDATE_ACHIEVEMENT } from 'queries/achievements'
import { updateAchievement, updateAchievementVariables } from '__generatedTypes__/updateAchievement'
import { findAchievement, findAchievementVariables } from '__generatedTypes__/findAchievement'
import Loader from 'components/Loader'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { PlatformSlug } from 'constants/consts'
import { toast } from 'utils/toast'
import * as classes from '../styles'
import { AwardsTabs, QUERY_PARAM } from '../consts'
import { Form, FormValues } from './Form'
import { MEMBERSHIP_EXTENSION_AWARD_TYPE, MAP_ENUM_TO_TITLE, getAwardId, getAwardTitle, getLevelTitle } from './consts'

export const AchievementEdit = () => {
  const { achievementId } = useParams()
  const { navigate } = useLink()

  const { data, loading } = useQuery<findAchievement, findAchievementVariables>(FIND_ACHIEVEMENT, {
    fetchPolicy: 'network-only',
    variables: {
      id: String(achievementId),
    },
    skip: !achievementId,
  })
  const [updateAchievement] = useMutation<updateAchievement, updateAchievementVariables>(UPDATE_ACHIEVEMENT)
  const handleSubmit = async (values: FormValues) => {
    const { awardType: awardTypeOption, type, level, ...rest } = values

    const { awardType, awardTypeValue } = MEMBERSHIP_EXTENSION_AWARD_TYPE[awardTypeOption?.id]

    try {
      await updateAchievement({
        variables: {
          updateAchievementInput: {
            ...rest,
            type: type.id,
            awardType,
            awardTypeValue,
            level: Number(level.id),
          },
        },
      })

      navigate(cmsRoutes.ACHIEVEMENTS, undefined, {
        searchParams: createSearchParams({
          [QUERY_PARAM]: slugify(AwardsTabs.ACHIEVEMENTS, { lower: true }),
        }),
      })

      toast.success('Achievement updated successfully')
    } catch (error) {
      toast.error('Failed to update achievement')
    }
  }

  const initialValues = useMemo(() => {
    const achievement = data?.findAchievement

    if (!achievement) {
      return
    }

    const { type, awardType, awardTypeValue, level, ...rest } = achievement

    return {
      ...omit(rest, '__typename'),
      platform: {
        id: rest.platform.id,
      },
      type: {
        id: type,
        title: MAP_ENUM_TO_TITLE[type],
      },
      awardType: {
        id: getAwardId(awardType, awardTypeValue),
        title: getAwardTitle(awardType, awardTypeValue),
      },
      level: {
        id: String(level),
        title: getLevelTitle(level),
      },
    }
  }, [data])

  if (loading) {
    return <Loader />
  }

  const platform = data?.findAchievement.platform.slug as PlatformSlug

  return (
    <section css={classes.container}>
      <Typography css={classes.title} variant="title3">
        Achievements
      </Typography>
      <Form initialValues={initialValues} onSubmit={handleSubmit} platform={platform} />
    </section>
  )
}
